import { PLAN_PARAMS } from '@trip-ux/constants';
import { first, get, gt, gte, isEmpty, isEqual, toNumber } from 'lodash';
import React, { FC, useState, useMemo } from 'react';
import { ActionButton } from '@manulife/mux';
import { Reload } from '@manulife/mux-cds-icons';
import { attributesNeeded, mqTaken, isPlanCardLoading, invalidCalculatedTopUpEndDate } from './card.utils';
import { MQButton } from './MQButton';
import { ProductCardAttributesValues, ProductCardProps } from './ProductCard';
import { ProductCardAttributes, ProductCardDropdown } from './ProductCardAttributes';
import { Progress } from '../Progress';
import { COVERME_TRAVELLER_TYPES, ReduxStore, isAgency, organization, isBroker } from '@trip-ux/redux-store';
import { sanitizeDOM } from '@trip-ux/common';
import { useSelector } from 'react-redux';

const MQButtonComponent = ({ isMqTaken, isMqEligible, genericLabels, isMQForReview, mqAction, paxId, variant, id }) => {
  return (
    <>
      {!isMqTaken && isMqEligible && <p className="h2">{genericLabels.mqAlert}</p>}
      {!isMqEligible && <h3 className="my-2 product-amount">{genericLabels.mqNotEligible}</h3>}
      <MQButton
        id={`${id}-mq-button`}
        genericLabels={genericLabels}
        isMqTaken={isMQForReview}
        mqAction={mqAction}
        paxId={paxId}
        variant={variant}
      />
    </>
  );
};

const AgencyProductAmount = ({ tripDays, values, amount, genericLabels, tripCost, agency, premium }) => {
  return (
    <>
      {agency && tripCost && isEmpty(tripDays) && (
        <div>
          {gte(toNumber(values.tripCost), 0) || premium ? (
            <label className={premium ? 'my-2 product-amount' : 'my-2 trip-cost-label'} data-testid="product-amount">
              {premium ? amount : genericLabels.coverageAmtRequired}
            </label>
          ) : (
            <label className="my-2 trip-cost-label" data-testid="product-amount">
              {genericLabels.tripCostRequired}
            </label>
          )}
        </div>
      )}
    </>
  );
};

const ProductAmountComponent = ({ tripDays, values, amount, genericLabels, tripCost, agency, premium }) => {
  return (
    <>
      {!isEmpty(tripDays) && (
        <div>
          {gt(values.daysPerTrip, 0) ? (
            <label className="my-2 product-amount" data-testid="product-amount">
              {amount}
            </label>
          ) : (
            <label className="my-2 trip-days-label" data-testid="product-amount">
              {genericLabels.tripDaysRequired}
            </label>
          )}
        </div>
      )}
      {!tripCost && amount && isEmpty(tripDays) && (
        <label className="my-2 product-amount" data-testid="product-amount">
          {amount}
        </label>
      )}

      {!agency && tripCost && isEmpty(tripDays) && (
        <div>
          {gte(toNumber(values.tripCost), 0) ? (
            <label className="my-2 product-amount" data-testid="product-amount">
              {amount}
            </label>
          ) : (
            <label className="my-2 trip-cost-label" data-testid="product-amount">
              {genericLabels.tripCostRequired}
            </label>
          )}
        </div >
      )}
      <AgencyProductAmount
        tripDays={tripDays}
        values={values}
        amount={amount}
        genericLabels={genericLabels}
        tripCost={tripCost}
        agency={agency}
        premium={premium}
      />
    </>
  );
};

const isTripCostAndCoverageAmount = (isTripCostNeeded, params, isCoverageAmountNeeded) => {
  return (isTripCostNeeded && isEmpty(params.tripCost)) || (isCoverageAmountNeeded && isEmpty(params.coverageAmount));
};

const RefreshButton = ({
  onParamCalculate,
  id,
  params,
  setDisableRefresh,
  code,
  disableRefresh,
  tripCostMaxAmt,
  isMqTakenAndEligible,
  isPlanAlreadyCalculated,
  genericLabels,
  tripDaysMaxTopUp,
  tripDaysMinTopUp,
  isCoverageAmountNeeded,
  isTripCostNeeded,
  tripStartDate,
  applicationDate,
  dateFormat
}) => {
  return (
    <ActionButton
      className="btn btn-refresh mx-auto p-0 my-2"
      icon={<Reload />}
      id={`${id}-calculate`}
      data-testid={`${id}-calculate`}
      onClick={e => {
        e.preventDefault();
        onParamCalculate(params, code);
        setDisableRefresh(true);
      }}
      disabled={
        disableRefresh ||
        (isEmpty(params.daysPerTrip) && isEmpty(params.tripCost)) ||
        gt(toNumber(params.tripCost), toNumber(tripCostMaxAmt)) ||
        isTripCostAndCoverageAmount(isTripCostNeeded, params, isCoverageAmountNeeded) ||
        gt(toNumber(params.daysPerTrip) + tripDaysMinTopUp, tripDaysMaxTopUp) ||
        !isMqTakenAndEligible ||
        invalidCalculatedTopUpEndDate(
          tripStartDate,
          applicationDate,
          dateFormat,
          tripDaysMinTopUp,
          toNumber(params.daysPerTrip)
        )
      }
      aria-disabled={disableRefresh ? 'true' : 'false'}
      type="button">
      {genericLabels.calculatePremium}
    </ActionButton>
  );
};

const noCoverageAmountAndTripCost = (hasCoverageAmount, tripCost) => {
  return !hasCoverageAmount || !tripCost;
};

const ProductCardAttributesConditional = ({
  newVal,
  params,
  setParams,
  setDisableRefresh,
  type,
  tripDays,
  tripCost,
  onParamChange,
  onChangeThreshold,
  isRefreshButtonNeeded,
  tripDaysMinTopUp,
  tripDaysMaxTopUp,
  hasCoverageAmount
}) => {
  if (!isEqual(params, newVal)) {
    setParams(newVal);
    setDisableRefresh(false);
  }

  // Only include keys with non-empty values for onParamChange
  const allKeys = ['deductible', 'coverageAmount', 'daysPerTrip', 'sumInsuredAfter', 'tripCost'];
  const normalizedVal = allKeys.reduce((acc, key) => {
    const value = newVal[key];
    if (value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});

  if (!isRefreshButtonNeeded && shouldRequestQuote(type, tripDays, tripCost, params)) {
    onParamChange(normalizedVal);
  }

  if (
    isEqual(type, PLAN_PARAMS.DEDUCTIBLE) &&
    newVal.daysPerTrip &&
    !gt(toNumber(params.daysPerTrip) + tripDaysMinTopUp, tripDaysMaxTopUp)
  ) {
    onParamChange(normalizedVal);
    setDisableRefresh(true);
  }

  if (isEqual(type, PLAN_PARAMS.COVERAGE_AMOUNT)) {
    onChangeThreshold(newVal.coverageAmount);
    if (newVal.tripCost && noCoverageAmountAndTripCost(hasCoverageAmount, tripCost)) {
      onParamChange(normalizedVal);
      setDisableRefresh(false);
    }
  }
};

const isRefreshNeeded = ({ calculateNeeded, tripCost, isTripDaysNeeded }) =>
  (calculateNeeded && tripCost) || (calculateNeeded && isTripDaysNeeded);

const shouldRequestQuote = (
  type: string,
  tripDays: ProductCardDropdown[],
  tripCost: boolean,
  params: ProductCardAttributesValues
): boolean => {
  const isTripDaysSelected = PLAN_PARAMS.TRIP_DAYS in params;

  switch (type) {
    case PLAN_PARAMS.DEDUCTIBLE:
      return isEmpty(tripDays) || isTripDaysSelected;
    case PLAN_PARAMS.TRIP_DAYS:
      return true;
    case PLAN_PARAMS.COVERAGE_AMOUNT: {
      if (tripCost) {
        return false;
      } else {
        return isEmpty(tripDays) || isTripDaysSelected;
      }
    }
    case PLAN_PARAMS.TRIP_COST:
    default:
      return false;
  }
};

export const CoverMeProductCard: FC<
  ProductCardProps & {
    setShowDetails: (details: boolean) => void;
  }
> = ({
  code,
  className,
  name,
  summary,
  isMqRequired,
  isMqTaken,
  genericLabels,
  values,
  onParamChange,
  deductibles,
  sumInsuredAfterNeeded,
  tripDays,
  coverageAmounts,
  coverageAmtKey,
  id,
  amount,
  mqAction,
  onParamCalculate,
  paxId,
  setShowDetails,
  variant,
  tripCost,
  tripCostMaxAmt,
  paxTripCostValue,
  isMqEligible,
  onChangeThreshold,
  quote,
  travellersPlan,
  travellerType,
  tripDaysMaxTopUp,
  tripDaysMinTopUp,
  daysPerTrip,
  viewDetailsRef,
  tripStartDate,
  applicationDate,
  dateFormat,
  tooltips,
  language,
  premium
}) => {
    const { organizationCategory } = useSelector((store: ReduxStore) => ({
      organizationCategory: get(store, 'userProfile.organizationCategory')
    }));
    const [params, setParams] = useState(values);
    const calculateNeeded = useMemo(
      () => attributesNeeded(deductibles, tripDays, sumInsuredAfterNeeded, tripCost, daysPerTrip),
      [deductibles, tripDays, sumInsuredAfterNeeded, tripCost, daysPerTrip]
    );
    const [disableRefresh, setDisableRefresh] = useState(true);
    const isMqTakenAndEligible = mqTaken(isMqRequired, isMqTaken) && isMqEligible;
    const isMQForReview = isMqTaken || !isMqEligible;
    const planSelection = get(travellersPlan, 'tripParams');
    //  to check whether button label should be calculate or recalculate
    const isPlanAlreadyCalculated =
      planSelection && first(travellersPlan.tripParams.filter(param => param.plan === code));
    const isTripDaysNeeded = travellerType === COVERME_TRAVELLER_TYPES.EXISTING_PLAN;
    const isRefreshButtonNeeded = isRefreshNeeded({ calculateNeeded, tripCost, isTripDaysNeeded });
    const disabledOnCalculate = get(quote, 'calculate');
    const hasCoverageAmount = !isEmpty(coverageAmounts);
    return (
      <div
        className={`product-card-content d-flex flex-column alt-primary-semi-dark-brd-active
              ${className}`}>
        <h3 className="plan-card-name" dangerouslySetInnerHTML={{ __html: sanitizeDOM(name) }} />
        <div className="text-left p" dangerouslySetInnerHTML={{ __html: sanitizeDOM(summary) }} />
        <a
          href="#view"
          ref={viewDetailsRef}
          className="primary-clr primary-clr-hover my-2 text-left"
          onClick={e => {
            e.preventDefault();
            setShowDetails(true);
          }}
          data-testid={`${id}-view-details`}
          id={`${id}-view-details`}
          aria-label={`${genericLabels.viewDetailsAriaLabel} ${name}`}
          aria-labelledby={`${genericLabels.viewDetailsAriaLabel} ${name}`}>
          {genericLabels.viewDetails}
        </a>
        <div className="product-card-content-grow">
          <ProductCardAttributes
            genericLabels={genericLabels}
            values={params}
            onAction={(type, val) => {
              const newVal = { ...params, [type]: val };
              ProductCardAttributesConditional({
                newVal,
                params,
                setParams,
                setDisableRefresh,
                type,
                tripDays,
                tripCost,
                onParamChange,
                onChangeThreshold,
                isRefreshButtonNeeded,
                tripDaysMinTopUp,
                tripDaysMaxTopUp,
                hasCoverageAmount
              });
            }}
            disabledOnCalculate={disabledOnCalculate}
            deductibles={deductibles}
            sumInsuredAfterNeeded={sumInsuredAfterNeeded}
            tripDays={tripDays}
            tripCost={tripCost}
            tripCostMaxAmt={tripCostMaxAmt}
            paxTripCostValue={paxTripCostValue}
            id={id}
            coverageAmount={get(coverageAmounts, coverageAmtKey)}
            isMQtaken={isMqTakenAndEligible}
            travellerType={travellerType}
            tripDaysMaxTopUp={tripDaysMaxTopUp}
            tripDaysMinTopUp={tripDaysMinTopUp}
            hasCoverageAmount={hasCoverageAmount}
            tripStartDate={tripStartDate}
            applicationDate={applicationDate}
            dateFormat={dateFormat}
            tooltips={tooltips}
            language={language}
          />
          {isRefreshButtonNeeded && (
            <RefreshButton
              onParamCalculate={onParamCalculate}
              setDisableRefresh={setDisableRefresh}
              disableRefresh={disableRefresh || disabledOnCalculate}
              params={params}
              code={code}
              tripCostMaxAmt={tripCostMaxAmt}
              isMqTakenAndEligible={isMqTakenAndEligible}
              isPlanAlreadyCalculated={isPlanAlreadyCalculated}
              genericLabels={genericLabels}
              id={id}
              tripDaysMaxTopUp={tripDaysMaxTopUp}
              tripDaysMinTopUp={tripDaysMinTopUp}
              isCoverageAmountNeeded={hasCoverageAmount}
              isTripCostNeeded={tripCost}
              tripStartDate={tripStartDate}
              applicationDate={applicationDate}
              dateFormat={dateFormat}
            />
          )}
        </div>
        {isPlanCardLoading(travellersPlan, quote, paxId, code) ? (
          <div aria-labelledby="calculateSpinnerLabel" aria-live="polite">
            <Progress isFloating={false} className="calculate-spinner" />
            <p id="calculateSpinnerLabel">{genericLabels.calculatePremiumSpinner}</p>
          </div>
        ) : (
          isMqTakenAndEligible && (
            <div aria-live="polite">
              <ProductAmountComponent
                tripDays={tripDays}
                values={values}
                amount={amount}
                agency={isAgency(organizationCategory)}
                premium={premium}
                genericLabels={genericLabels}
                tripCost={tripCost}
              />
            </div>
          )
        )}
        {isMqRequired && (
          <MQButtonComponent
            isMqTaken={isMqTaken}
            isMqEligible={isMqEligible}
            genericLabels={genericLabels}
            isMQForReview={isMQForReview}
            mqAction={mqAction}
            paxId={paxId}
            variant={variant}
            id={id}
          />
        )}
      </div>
    );
  };
CoverMeProductCard.displayName = 'CoverMeProductCard';